import type { ReactNode } from 'react';
import { tv } from '@app/ui/_lib/utils';
import { mediumLargeXLargeValues } from '../_lib/designTypesStore';
import { isValidRichText } from '../_lib/isValidRichText';
import { ComponentButton, type ComponentButtonProps } from '../Inputs/Button';
import { Container } from '../Container';
import { Text } from '../Text';

const styles = tv({
	slots: {
		base: 'max-w-max-md flex flex-col gap-responsive-md',
	},
	variants: {
		layout: {
			center: {
				base: 'mx-auto text-center items-center',
			},
			left: {
				base: '',
			},
		},
		size: {
			xLarge: {
				base: 'max-w-max-lg',
			},
			large: {},
			medium: {},
		},
	},
});

export type TitleSectionProps = {
	className?: string;
	copy?: ReactNode;
	layout?: 'center' | 'left';
	overline?: string;
	size?: (typeof mediumLargeXLargeValues)[number];
	tabs?: ReactNode;
	title: ReactNode;
	buttons?: ComponentButtonProps[];
};

export function TitleSection(props: TitleSectionProps) {
	const layout = props.layout ?? 'center';
	const size = props.size ?? 'large';
	const { base } = styles({ layout, size });

	return (
		<Container className={props.className} data-component='TitleSection'>
			<div className={base()}>
				{props.tabs}

				<div className={`flex flex-col items-start gap-responsive-sm`}>
					{props.overline && (
						<Text as='span' className={`text-text-brand w-full`} styleType='label-md'>
							{props.overline}
						</Text>
					)}
					<Text as='h2' className={`w-full`} styleType={getTitleSize(size)}>
						{props.title}
					</Text>
					{isValidRichText(props.copy) && (
						<Text className={`opacity-80`} styleType='body-lg'>
							{props.copy}
						</Text>
					)}
				</div>
				{props.buttons && props.buttons.length > 0 && (
					<div className={`inline-flex flex-wrap gap-2`}>
						{props.buttons.map((item, index) => (
							<ComponentButton key={index} {...item} />
						))}
					</div>
				)}
			</div>
		</Container>
	);
}

function getTitleSize(size: TitleSectionProps['size']) {
	switch (size) {
		case 'medium':
			return 'heading-lg';
		case 'large':
			return 'heading-xl';
		case 'xLarge':
			return 'heading-2xl';
		default:
			return 'heading-lg';
	}
}
